import {
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAInput,
  GoASpacer,
} from "@abgov/react-components";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { formatPhoneNumber, formatPostalCode, isValidPhone } from "../../utils/formUtils";
import { useState } from "react";

interface ContactProps {
  address: string;
  setAddress: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  province: string;
  setProvince: (value: string) => void;
  primaryPhone: string;
  setPrimaryPhone: (value: string) => void;
  alternatePhone: string;
  setAlternatePhone: (value: string) => void;
  postalCodeErrorMessage: string;
  setPostalCodeErrorMessage: (value: string) => void;
  primaryPhoneErrorMessage: string;
  setPrimaryPhoneErrorMessage: (value: string) => void;
  alternatePhoneErrorMessage: string;
  setAlternatePhoneErrorMessage: (value: string) => void;
}

export const ContactComponent = ({
  address,
  setAddress,
  city,
  setCity,
  country,
  setCountry,
  postalCode,
  setPostalCode,
  province,
  setProvince,
  primaryPhone,
  setPrimaryPhone,
  alternatePhone,
  setAlternatePhone,
  postalCodeErrorMessage,
  setPostalCodeErrorMessage,
  primaryPhoneErrorMessage,
  setPrimaryPhoneErrorMessage,
  alternatePhoneErrorMessage,
  setAlternatePhoneErrorMessage,
}: ContactProps) => {
  const stateConfiguration = useSelector(
    (state: RootState) => state.configuration
  );

  //new state to determine country code based on state/prov selection
  const [countryCode, setCountryCode] = useState("");

  return (
    <div
      style={{
        width: "343px",
        display: "flex",
        flexDirection: "column",
        gap: "var(--goa-space-m)",
      }}
    >
      <div style={{ width: "345px", display: "flex" }}>
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem label="Street address">
            <GoAInput
              name="address"
              type="text"
              value={address}
              width="100%"
              maxLength={25}
              onChange={(_, value) => {
                setAddress(value);
              }}
            />
          </GoAFormItem>
        </div>
      </div>
      <GoASpacer vSpacing="xl" />
      <div
        style={{ width: "345px", display: "flex", gap: "var(--goa-space-m)" }}
      >
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem label="City or town">
            <GoAInput
              name="city"
              type="text"
              value={city}
              width="100%"
              maxLength={25}
              onChange={(_, value) => {
                setCity(value);
              }}
            />
          </GoAFormItem>
        </div>
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem label="Country">
            <GoAInput
              name="country"
              type="text"
              value={country}
              width="100%"
              maxLength={50}
              onChange={(_, value) => {
                setCountry(value);
              }}
            />
          </GoAFormItem>
        </div>
      </div>
      <GoASpacer vSpacing="xl" />
      <div
        style={{ width: "345px", display: "flex", gap: "var(--goa-space-m)" }}
      >
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem label="Province or state" labelSize="regular">
            <GoADropdown
              name="province"
              value={province}
              width="100%"
              maxHeight="175px"
              relative={true}
              onChange={(_, value) => {
                if (value) {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const selectedProvince =
                    stateConfiguration.configuration.provinceOrState.find(
                      (item) => String(item.id) === selectedValue // Convert `item.id` to string
                    );

                  const countryCode = selectedProvince
                    ? selectedProvince.value.split(",")[1]?.trim()
                    : "";
                  setCountryCode(countryCode);
                  //if there is already a postal code entered...make sure it gets formatted correctly if it's Canadian
                  if (countryCode.toLowerCase() == "ca" && postalCode) {
                    const formattedPostalCode = formatPostalCode(
                      postalCode,
                      countryCode
                    );
                    setPostalCode(formattedPostalCode);
                  }
                  setProvince(value.toString());
                } else {
                  setProvince("");
                }
              }}
              placeholder="—Select—"
            >
              {stateConfiguration.configuration.provinceOrState.map(
                ({ id, value }: { id: number; value: string }) => {
                  return (
                    <GoADropdownItem
                      key={id}
                      value={id.toString()}
                      label={value}
                    />
                  );
                }
              )}
            </GoADropdown>
          </GoAFormItem>
        </div>
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem
            label="Postal or zip code"
            error={
              postalCodeErrorMessage.length > 0 ? postalCodeErrorMessage : ""
            }
          >
            <GoAInput
              name="postalCode"
              type="text"
              value={postalCode}
              width="100%"
              maxLength={10}
              error={postalCodeErrorMessage.length > 0}
              onChange={(_, value) => {
                if (value) {
                  const formattedValue = formatPostalCode(value, countryCode);
                  setPostalCode(formattedValue);
                }
              }}
            />
          </GoAFormItem>
        </div>
      </div>
      <GoASpacer vSpacing="2xl" />
      <div
        style={{ width: "345px", display: "flex", gap: "var(--goa-space-m)" }}
      >
        <div style={{ flex: 1, maxHeight: "32px" }}>
          <GoAFormItem label="Primary phone" error={ primaryPhoneErrorMessage.length > 0 ? primaryPhoneErrorMessage : "" } maxWidth="200px" >
            <GoAInput
              name="primaryPhone"
              type="tel"
              maxLength={14}
              placeholder="(xxx) xxx-xxxx"
              value={formatPhoneNumber(primaryPhone ?? "")}
              width="100%"
              error={primaryPhoneErrorMessage.length > 0}
              onChange={(_, value) => {
                if (value) {
                  const formattedValue = formatPhoneNumber(value);
                  setPrimaryPhone(formattedValue);
                  if(isValidPhone(formattedValue))
                      setPrimaryPhoneErrorMessage("");
                    else{
                      setPrimaryPhoneErrorMessage(value);
                    }
								} else {
									setPrimaryPhoneErrorMessage("");
                }
              }}
            />
          </GoAFormItem>
        </div>
        <div>
          <GoAFormItem
            label="Alternate phone"
            error={
              alternatePhoneErrorMessage.length > 0
                ? alternatePhoneErrorMessage
                : ""
            }
            requirement="optional"
          >
            <GoAInput
              name="alternatePhone"
              type="tel"
              value={formatPhoneNumber(alternatePhone ?? "")}
              width="100%"
              maxLength={14}
              placeholder="(xxx) xxx-xxxx"
              error={alternatePhoneErrorMessage.length > 0 }
              onChange={(_, value) => {
                if (value) {
                  const formattedValue = formatPhoneNumber(value);
                  setAlternatePhone(formattedValue);
                  if(isValidPhone(formattedValue))
                      setAlternatePhoneErrorMessage("");
                    else{
                      setAlternatePhoneErrorMessage(value)
                    }
								} else {
									setAlternatePhoneErrorMessage("");
                }
              }}
            />
          </GoAFormItem>
        </div>
      </div>
    </div>
  );
};
